import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';
import { FormField } from '@shared/components/FormField';
import type { Platform } from '@shared/types';

interface CreateProjectFormProps {
  onSubmit: (data: { name: string; description: string; platform_id?: string; due_date?: string }) => Promise<void>;
  onCancel: () => void;
}

export function CreateProjectForm({ onSubmit, onCancel }: CreateProjectFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [platformId, setPlatformId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    apiClient.getPlatforms().then(setPlatforms).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        name,
        description,
        platform_id: platformId || undefined,
        due_date: dueDate || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const inputClass = 'mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-accent-500 focus:ring-accent-500 sm:text-sm';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Project Name" required htmlFor="create-project-name">
        <input id="create-project-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., 2025 Spotify Royalty Audit" className={inputClass} required />
      </FormField>
      <FormField label="Description" htmlFor="create-project-desc">
        <textarea id="create-project-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Brief description of the audit engagement" className={inputClass} />
      </FormField>
      <FormField label="Platform" required htmlFor="create-project-platform">
        <select id="create-project-platform" value={platformId} onChange={(e) => setPlatformId(e.target.value)} className={inputClass} required>
          <option value="">Select a platform...</option>
          {platforms.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </FormField>
      <FormField label="Due Date" htmlFor="create-project-due-date">
        <input id="create-project-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} min={today} className={inputClass} />
      </FormField>
      <div className="flex justify-end space-x-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 rounded-md disabled:opacity-50 transition-colors">
          {isSubmitting ? 'Creating...' : 'Create Project'}
        </button>
      </div>
    </form>
  );
}
